import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminService } from '../../services/adminService';
import { IngestionHealthStatus, CloudProvider } from '../../types/admin';
import { LoadingState } from '../common/LoadingState';
import { ErrorState } from '../common/ErrorState';

/**
 * Ingestion Health Tab
 * Implements Requirement 172 AC6: display ingestion health per cloud
 * account with status, last-checked time, error details, and data
 * freshness - built on the same cloud_accounts data Cloud Onboarding
 * already verifies, not a separate mock.
 *
 * "Records processed/failed" and "next sync" were deliberately dropped:
 * this platform queries cost data live from each provider on demand rather
 * than running a batch ingestion pipeline, so there's no real sync job or
 * record count to report honestly for those.
 */
export const IngestionHealthTab: React.FC = () => {
  const {
    data: healthData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['ingestionHealth'],
    queryFn: () => adminService.getIngestionHealth(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const [verifyingId, setVerifyingId] = useState<string | null>(null);

  const { data: onboardingMetrics } = useQuery({
    queryKey: ['onboardingMetrics'],
    queryFn: () => adminService.getOnboardingMetrics(),
    refetchInterval: 60000,
  });

  const handleVerifyNow = async (id: string) => {
    setVerifyingId(id);
    try {
      await adminService.verifyCloudAccount(id);
      refetch();
    } catch (err: any) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.error || err?.message || 'Failed to verify account');
    } finally {
      setVerifyingId(null);
    }
  };

  const getStatusIcon = (status: string) => {
    const icons: Record<string, React.ReactNode> = {
      [IngestionHealthStatus.HEALTHY]: (
        <svg className="h-6 w-6 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
          />
        </svg>
      ),
      [IngestionHealthStatus.WARNING]: (
        <svg className="h-6 w-6 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      ),
      [IngestionHealthStatus.ERROR]: (
        <svg className="h-6 w-6 text-rose-500" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clipRule="evenodd"
          />
        </svg>
      ),
      [IngestionHealthStatus.UNKNOWN]: (
        <svg className="h-6 w-6 text-muted-foreground" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
            clipRule="evenodd"
          />
        </svg>
      ),
    };
    return icons[status] || icons[IngestionHealthStatus.UNKNOWN];
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      [IngestionHealthStatus.HEALTHY]: 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-900 dark:text-emerald-200',
      [IngestionHealthStatus.WARNING]: 'bg-amber-500/10 text-amber-600 dark:bg-amber-900 dark:text-amber-200',
      [IngestionHealthStatus.ERROR]: 'bg-rose-500/10 text-rose-600 dark:bg-rose-900 dark:text-rose-200',
      [IngestionHealthStatus.UNKNOWN]: 'bg-secondary text-foreground dark:bg-secondary dark:text-muted-foreground',
    };

    const labels: Record<string, string> = {
      [IngestionHealthStatus.HEALTHY]: 'HEALTHY',
      [IngestionHealthStatus.WARNING]: 'WARNING',
      [IngestionHealthStatus.ERROR]: 'ERROR',
      [IngestionHealthStatus.UNKNOWN]: 'NOT YET VERIFIED',
    };

    return (
      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${styles[status] || styles[IngestionHealthStatus.UNKNOWN]}`}>
        {labels[status] || status.toUpperCase()}
      </span>
    );
  };

  const getProviderIcon = (provider: CloudProvider) => {
    const icons = {
      [CloudProvider.AWS]: '☁️',
      [CloudProvider.AZURE]: '🔷',
      [CloudProvider.GCP]: '🔴',
    };
    return icons[provider] || '🔌';
  };

  const getDataFreshnessColor = (hours: number | null) => {
    if (hours === null) return 'text-muted-foreground';
    if (hours <= 6) return 'text-emerald-600 dark:text-emerald-400';
    if (hours <= 12) return 'text-amber-600 dark:text-amber-400';
    return 'text-rose-600 dark:text-rose-400';
  };

  // FR-ONB-032: tells the admin what to actually do about a regression,
  // mirroring the exact wording the connector-health scheduler already puts
  // in the Jira ticket - so the dashboard and the ticket never disagree.
  const DRIFT_WORDING: Record<string, { label: string; action: string }> = {
    customer_revoked_access: { label: 'Access revoked on the customer side', action: 'Ask the customer to re-grant the role/export - it was likely changed or removed outside the platform.' },
    platform_bug: { label: 'Looks like our own bug', action: 'Investigate the platform code before contacting the customer.' },
    transient: { label: 'Likely transient', action: 'Wait for the next scheduled check before escalating.' },
    unknown: { label: 'Unrecognized failure', action: 'Needs manual triage.' },
  };

  if (isLoading) {
    return <LoadingState message="Loading ingestion health..." />;
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load ingestion health"
        message="Unable to fetch ingestion health data. Please try again."
        onRetry={refetch}
      />
    );
  }

  const healthySystems = healthData?.filter((h) => h.status === IngestionHealthStatus.HEALTHY).length || 0;
  const totalSystems = healthData?.length || 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Ingestion Health</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Real per-account connection status, pulled from the same checks Cloud Onboarding runs
          </p>
        </div>
        <div className="text-right">
          <div className="text-base font-semibold tracking-tight text-foreground">
            {healthySystems}/{totalSystems}
          </div>
          <div className="text-sm text-muted-foreground">Healthy Accounts</div>
        </div>
      </div>

      {totalSystems === 0 && (
        <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground shadow-sm dark:border-border dark:bg-card dark:text-muted-foreground/70">
          No cloud accounts onboarded yet for this tenant - add one in Cloud Onboarding to see its health here.
        </div>
      )}

      {/* Health Cards */}
      <div className="space-y-4">
        {healthData?.map((health) => (
          <div
            key={health.id}
            className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {getStatusIcon(health.status)}
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{getProviderIcon(health.provider)}</span>
                    <h3 className="text-sm font-semibold text-foreground">
                      {health.accountName}
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {health.provider.toUpperCase()} • {health.accountId}
                  </p>
                </div>
              </div>
              {getStatusBadge(health.status)}
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <div className="text-sm text-muted-foreground">Last Checked</div>
                <div className="mt-1 text-sm font-medium text-foreground">
                  {health.lastSyncTime ? new Date(health.lastSyncTime).toLocaleString() : 'Never'}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Data Freshness</div>
                <div className={`mt-1 text-sm font-medium ${getDataFreshnessColor(health.dataFreshness)}`}>
                  {health.dataFreshness === null ? 'Unknown' : `${health.dataFreshness}h ago`}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Completeness</div>
                <div className="mt-1 text-sm font-medium text-foreground">
                  {health.completenessCount === null ? 'Not yet checked' : `${health.completenessCount.toLocaleString()} rows/blobs seen`}
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Correctness</div>
                <div className={`mt-1 text-sm font-medium ${health.correctnessOk === null ? 'text-muted-foreground' : health.correctnessOk ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  {health.correctnessOk === null ? 'Not yet checked' : health.correctnessOk ? 'Schema looks correct' : (health.correctnessDetail || 'Schema check failed')}
                </div>
              </div>
            </div>

            {health.driftClassification && (
              <div className="mt-4 rounded-md bg-amber-50 p-4 dark:bg-amber-900/20">
                <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Drift detected: {DRIFT_WORDING[health.driftClassification]?.label || health.driftClassification}
                </h3>
                <div className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                  {DRIFT_WORDING[health.driftClassification]?.action}
                </div>
              </div>
            )}

            {health.errorDetails && (
              <div className="mt-4 rounded-md bg-rose-50 p-4 dark:bg-rose-900/20">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-rose-400" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-rose-800 dark:text-rose-200">Error Details</h3>
                    <div className="mt-2 text-sm text-rose-700 dark:text-rose-300">{health.errorDetails}</div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => handleVerifyNow(health.id)}
                disabled={verifyingId === health.id}
                className="rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground/80 hover:bg-muted/30 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 dark:border-border dark:bg-secondary dark:text-muted-foreground dark:hover:bg-secondary"
              >
                {verifyingId === health.id ? 'Verifying...' : 'Verify Now'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* SLA Information */}
      <div className="rounded-lg border border-border bg-muted/30 p-4 dark:border-border dark:bg-card">
        <h3 className="text-sm font-semibold text-foreground">Ingestion SLA (target)</h3>
        <div className="mt-4 space-y-2 text-sm text-muted-foreground">
          <p>• p50 data freshness target: ≤ 6 hours</p>
          <p>• p99 data freshness target: ≤ 24 hours</p>
        </div>
      </div>

      {/* Requirement 119 AC3 - real measured onboarding SLA, not just a target */}
      <div className="rounded-lg border border-border bg-muted/30 p-4 dark:border-border dark:bg-card">
        <h3 className="text-sm font-semibold text-foreground">Onboarding SLA (measured)</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <div className="text-sm text-muted-foreground">Time to first value (target: ≤ 14 days)</div>
            <div className={`mt-1 text-sm font-semibold ${onboardingMetrics?.timeToFirstValueTargetMet === false ? 'text-rose-600 dark:text-rose-400' : onboardingMetrics?.timeToFirstValueTargetMet === true ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
              {onboardingMetrics?.timeToFirstValueDays != null ? `${onboardingMetrics.timeToFirstValueDays} days` : 'Not yet measurable'}
            </div>
            {onboardingMetrics?.timeToFirstValueHours != null && (
              <div className="text-xs text-muted-foreground">({onboardingMetrics.timeToFirstValueHours}h from first account created to first verified)</div>
            )}
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Time to first automated savings (target: ≤ 45 days)</div>
            <div className={`mt-1 text-sm font-semibold ${onboardingMetrics?.timeToFirstAutomatedSavingsTargetMet === false ? 'text-rose-600 dark:text-rose-400' : onboardingMetrics?.timeToFirstAutomatedSavingsTargetMet === true ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
              {onboardingMetrics?.timeToFirstAutomatedSavingsDays != null ? `${onboardingMetrics.timeToFirstAutomatedSavingsDays} days` : 'No L1 execution yet'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
